import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';
import { calcBidPercentage } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { BidInput } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

/** Referent Options for rate override */
const Referent = {
    AWP: ReferentPriceTypes.AWP,
    ASP: ReferentPriceTypes.ASP,
    CONTRACT: 'Contract',
};

const USE_DEEPEST_RATE = 'USE_DEEPEST_RATE';

export const RateOverrideInputs = () => {
    const formik = useFormikContext();
    return (
        <>
            <div className="margin__bottom--medium font-bold--uppercase">Rate Override</div>
            <SuggestSingleSelectFormik
                id="referent_price_type"
                label="Referent Type"
                values={Object.values(Referent)}
                onChange={(newVal) =>
                    formik.setValues({
                        ...formik.values,
                        referent_price_type: newVal,
                        use_deepest_rate:
                            newVal === Referent.CONTRACT ? false : formik.values.use_deepest_rate,
                    })
                }
            />
            {formik.values.referent_price_type &&
                formik.values.referent_price_type !== Referent.CONTRACT && (
                    <BidInput
                        id="rate_override"
                        label="Rate Override"
                        required
                        referentPriceType={formik.values.referent_price_type}
                    />
                )}
            <CheckboxListFormik
                name="use_deepest_rate"
                label="Use Lesser Of Rates?"
                disabled={formik.values.referent_price_type === Referent.CONTRACT}
                options={[
                    {
                        value: USE_DEEPEST_RATE,
                        text: 'Use the lesser of rates',
                    },
                ]}
                values={formik.values.use_deepest_rate ? [USE_DEEPEST_RATE] : []}
                onChange={(newValue) =>
                    formik.setFieldValue('use_deepest_rate', newValue?.includes(USE_DEEPEST_RATE))
                }
            />
        </>
    );
};

RateOverrideInputs.initialValues = (routeRule) => {
    const baseValues = { use_deepest_rate: routeRule?.use_deepest_rate ?? false };
    const rateOverride =
        routeRule?.rate_override != null ? Number(routeRule.rate_override) : undefined;

    return rateOverride === CONTRACT_RATE
        ? { ...baseValues, referent_price_type: Referent.CONTRACT }
        : {
              ...baseValues,
              referent_price_type: routeRule?.referent_price_type ?? undefined,
              rate_override: calcBidPercentage(rateOverride),
          };
};

RateOverrideInputs.validationSchema = {
    referent_price_type: Yup.string().oneOf(Object.values(Referent)),
    rate_override: BidInput.validator().when('referent_price_type', {
        is: (referent) =>
            Object.values(Referent)
                .filter((val) => val !== Referent.CONTRACT)
                .includes(referent),
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    use_deepest_rate: Yup.bool().required(validationStrings.requiredField.errorMessage),
};

RateOverrideInputs.submitValues = (values) => {
    const baseValues = { use_deepest_rate: values.use_deepest_rate };
    if (values.referent_price_type == null) {
        return baseValues;
    }

    return values.referent_price_type === Referent.CONTRACT
        ? { ...baseValues, rate_override: CONTRACT_RATE }
        : {
              ...baseValues,
              referent_price_type: values.referent_price_type,
              rate_override: Decimal.div(values.rate_override, 100).toNumber(),
          };
};
